/**
 * Primary-identification requirements that depend on the selected country.
 *
 * Core rule (KYC): a non-passport government ID (driver's license, state ID,
 * government ID, alien ID) is only acceptable when it was issued in the country
 * the transaction is tied to (the selected payout country). A passport is the
 * only internationally-valid document. So if a user's stored/prefilled ID is a
 * non-passport issued in a different country than the one they select, they
 * must provide a passport instead.
 */

export const PASSPORT_ID_TYPE = "PAS";

// Maps legacy id type values to the canonical short codes used by the API and
// the identification UI. Kept as the single source of truth for normalization.
const LEGACY_ID_TYPE_MAP: Record<string, string> = {
	"state-id": "STA",
	"driver-license": "DRV",
	passport: "PAS",
	"government-id": "GOV",
	"alien-id": "ALN",
};

export function normalizeIdType(idType: string): string {
	return LEGACY_ID_TYPE_MAP[idType] || idType;
}

export function isPassport(idType: string): boolean {
	return normalizeIdType(idType) === PASSPORT_ID_TYPE;
}

/**
 * Whether a stored ID must be replaced with a passport for the selected country.
 *
 * True only when we have enough information to judge (an id type, its issue
 * country, and a selected payout country), the ID is NOT a passport, and its
 * issue country differs from the selected country.
 */
export function requiresPassportSwitch(args: {
	idType: string;
	idIssueCountry: string;
	payoutCountry: string;
}): boolean {
	const { idType, idIssueCountry, payoutCountry } = args;

	if (!idType || !idIssueCountry || !payoutCountry) {
		return false;
	}
	if (isPassport(idType)) {
		return false;
	}
	return idIssueCountry !== payoutCountry;
}
